import isEmpty from 'lodash/isEmpty';
import startsWith from 'lodash/startsWith';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import Dropdown from '../Dropdown';
import Button from '../Button';
import Tooltip from '../../common/components/Tooltip';
import NavigationSubItem from './NavigationSubItem';
import BetaBadge from '../BetaBadge';

const DropdownButton = forwardRef(({ onClick, icon, isSelected }, ref) => {
  return (
    <Button
      ref={ref}
      type="tertiary"
      icon={icon}
      onClick={onClick}
      className={isSelected ? 'bg-neutral-500 dark:bg-neutral-800' : ''}
    />
  );
});

DropdownButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.node.isRequired,
  isSelected: PropTypes.bool.isRequired,
};

const NavigationItem = ({
  testId,
  className,
  label,
  to,
  href,
  onClick,
  icon,
  items,
  isOpen,
  showTooltip,
  isBeta,
}) => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.dir();
  const isLanguageDirectionLTR = languageDirection === 'ltr';

  const location = useLocation();
  const isActive = startsWith(location.pathname, to);
  const isSelected = useMemo(() => {
    if (
      !isEmpty(items) &&
      items.find((item) => startsWith(location.pathname, item.to))
    ) {
      return true;
    }

    return false;
  }, [location, items]);

  const [isSubMenuVisible, setIsSubMenuVisible] = useState(false);
  const handleOnSubMenuToggle = () => setIsSubMenuVisible(!isSubMenuVisible);

  if (href) {
    return (
      <li
        data-testid={testId}
        className={`${className} ${isOpen ? '' : 'mx-auto'}`}
      >
        <Tooltip title={!isOpen && label} placement="auto-end">
          <Button type="tertiary" icon={icon} href={href} isCentered={false}>
            {isOpen && label}
          </Button>
        </Tooltip>
      </li>
    );
  }

  if (onClick) {
    return (
      <li
        data-testid={testId}
        className={`${className} ${isOpen ? '' : 'mx-auto'}`}
      >
        <Tooltip title={showTooltip && !isOpen && label} placement="auto-end">
          <Button
            type="tertiary"
            icon={icon}
            onClick={onClick}
            className={`${isOpen ? 'w-full' : ''}`}
            isCentered={false}
          >
            {isOpen && label}
          </Button>
        </Tooltip>
      </li>
    );
  }

  if (to) {
    return (
      <li
        data-testid={testId}
        className={`${className} ${isOpen ? '' : 'mx-auto'}`}
      >
        <Tooltip title={!isOpen && label} placement="auto-end">
          <Button
            type="tertiary"
            icon={icon}
            to={to}
            className={isActive ? 'bg-neutral-500 dark:bg-neutral-800' : ''}
            isCentered={false}
          >
            {isOpen && label}
          </Button>
        </Tooltip>
      </li>
    );
  }

  const activeId = useMemo(() => {
    if (!isEmpty(items)) {
      const matchingItem = items
        .filter((i) => location.pathname.startsWith(i.to))
        .sort((a, b) => b.to.length - a.to.length)[0];

      return matchingItem?.id;
    }

    return null;
  }, [location, items]);

  useEffect(() => {
    if (isOpen && isSelected) {
      setIsSubMenuVisible(true);
    }
  }, [isOpen, isSelected]);

  if (!isEmpty(items)) {
    return (
      <li
        data-testid={testId}
        className={`${className} ${isOpen ? '' : 'mx-auto'}`}
      >
        {isOpen ? (
          <>
            <Button
              type="tertiary"
              icon={icon}
              className={`${isOpen ? 'w-full' : ''}`}
              onClick={handleOnSubMenuToggle}
              isCentered={false}
            >
              <div
                className={`mie-4 ${isBeta ? 'cluster-3 items-center' : ''}`}
              >
                <span>{label}</span>
                {isBeta && <BetaBadge />}
              </div>
              <motion.div
                className="ml-auto"
                initial={false}
                animate={isSubMenuVisible ? { rotate: 90 } : { rotate: 0 }}
              >
                {isLanguageDirectionLTR ? (
                  <ChevronRightIcon className="w-5 h-5" />
                ) : (
                  <ChevronLeftIcon className="w-5 h-5" />
                )}
              </motion.div>
            </Button>
            <AnimatePresence initial={false}>
              {isSubMenuVisible && (
                <motion.ul
                  className="pis-0 overflow-hidden stack-1"
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={{
                    open: { height: 'auto' },
                    collapsed: { height: 0 },
                  }}
                >
                  {items?.map((item) => (
                    <NavigationSubItem
                      testId={item.testId}
                      key={item.to}
                      label={item.label}
                      to={item.to}
                    />
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </>
        ) : (
          <Dropdown
            as={DropdownButton}
            asProps={{
              isSelected,
            }}
            items={items}
            icon={icon}
            title={label}
            position="right-start"
            activeId={activeId}
            isBeta={isBeta}
          />
        )}
      </li>
    );
  }

  return null;
};

NavigationItem.propTypes = {
  testId: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  to: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    }),
  ),
  isOpen: PropTypes.bool,
  showTooltip: PropTypes.bool,
  isBeta: PropTypes.bool,
};

NavigationItem.defaultProps = {
  testId: '',
  className: '',
  items: [],
  to: null,
  href: null,
  onClick: null,
  icon: null,
  isOpen: false,
  showTooltip: true,
  isBeta: false,
};

export default NavigationItem;
